import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { azureBlobService, VideoMetadata } from '../services/azureBlobService';
import '../styles/VideoUpload.css';

interface VideoUploadProps {
  onUploadComplete?: (video: VideoMetadata) => void;
}

const VideoUpload: React.FC<VideoUploadProps> = ({ onUploadComplete }) => {
  const { user } = useAuth();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files: FileList) => {
    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      setUploadStatus('Please select a valid video file');
      return;
    }

    // Validate file size (max 100MB for demo)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      setUploadStatus('File size must be less than 100MB');
      return;
    }

    if (!user) {
      setUploadStatus('You must be logged in to upload videos');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadStatus('Uploading video...');

      const videoMetadata = await azureBlobService.uploadVideo(
        file,
        user.email,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      setUploadStatus('Upload completed successfully!');
      setUploadProgress(100);
      
      if (onUploadComplete) {
        onUploadComplete(videoMetadata);
      }

      // Reset form
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 2000);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('Upload failed. Please try again.');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="video-upload-container">
      <div className="upload-header">
        <h2>Upload Video</h2>
        <p>Drag and drop your video file or click to browse</p>
      </div>

      <div
        className={`upload-area ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!uploading ? openFileDialog : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={uploading}
        />

        {!uploading ? (
          <div className="upload-content">
            <div className="upload-icon">🎥</div>
            <h3>Choose a video file or drag it here</h3>
            <p>Supports: MP4, AVI, MOV, WMV (Max: 100MB)</p>
            <button className="browse-button" type="button">
              Browse Files
            </button>
          </div>
        ) : (
          <div className="upload-progress">
            <div className="progress-icon">📤</div>
            <h3>{uploadStatus}</h3>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p>{Math.round(uploadProgress)}% complete</p>
          </div>
        )}
      </div>

      {uploadStatus && !uploading && (
        <div className={`upload-message ${uploadStatus.includes('failed') || uploadStatus.includes('error') ? 'error' : 'success'}`}>
          {uploadStatus}
        </div>
      )}

      <div className="upload-tips">
        <h4>Tips for better uploads:</h4>
        <ul>
          <li>Use common video formats like MP4 for best compatibility</li>
          <li>Ensure stable internet connection for large files</li>
          <li>Compress videos if they're larger than 100MB</li>
          <li>Videos will be accessible from any device once uploaded</li>
        </ul>
      </div>
    </div>
  );
};

export default VideoUpload;